import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import configurationService from '../services/configurationService';
import './AdminConfiguration.css';

const CATEGORY_ICONS = {
    Stripe: '💳',
    MBWay: '📱',
    Email: '📧',
    GoogleCalendar: '📅',
    GoogleFit: '⌚',
    Notifications: '🔔',
    Gym: '🏋️'
};

const AdminConfiguration = ({ allowedCategories = null }) => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [configs, setConfigs] = useState([]);
    const [editValues, setEditValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(null);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        if (activeCategory) {
            loadCategoryConfigs(activeCategory);
        }
    }, [activeCategory]);

    const loadCategories = async () => {
        try {
            const data = await configurationService.getCategories();
            const filtered = Array.isArray(allowedCategories) && allowedCategories.length > 0
                ? (data || []).filter(c => allowedCategories.includes(c.id))
                : (data || []);

            setCategories(filtered);
            if (filtered.length > 0) {
                setActiveCategory(filtered[0].id);
            }
        } catch (error) {
            showMessage('error', 'Erro ao carregar categorias');
        } finally {
            setLoading(false);
        }
    };

    const loadCategoryConfigs = async (category) => {
        setLoading(true);
        try {
            const data = await configurationService.getByCategory(category);
            setConfigs(data);
            
            // Initialize edit values
            const values = {};
            data.forEach(c => {
                values[c.key] = c.value === '••••••••' ? '' : (c.value || '');
            });
            setEditValues(values);
        } catch (error) {
            showMessage('error', 'Erro ao carregar configurações');
        } finally {
            setLoading(false);
        }
    };

    const handleValueChange = (key, value) => {
        setEditValues(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updates = configs
                .filter(c => {
                    const newValue = editValues[c.key];
                    const oldValue = c.value === '••••••••' ? '' : (c.value || '');
                    // Um Redirect URI relativo guardado na base não serve à Google: é gravado
                    // (já completo, abaixo) mesmo que não tenha sido editado.
                    const relativeRedirect = c.key.endsWith(':RedirectUri')
                        && typeof newValue === 'string' && newValue.startsWith('/');
                    return (newValue !== oldValue && newValue !== '') || relativeRedirect;
                })
                .map(c => ({
                    key: c.key,
                    value: (() => {
                        const raw = editValues[c.key];
                        if (typeof raw !== 'string') return raw;

                        // If a RedirectUri is stored as a relative path, normalize to a full URL.
                        if (c.key.endsWith(':RedirectUri') && raw.startsWith('/')) {
                            return `${window.location.origin}${raw}`;
                        }
                        return raw;
                    })()
                }));

            if (updates.length === 0) {
                showMessage('info', 'Nenhuma alteração para guardar');
                return;
            }

            await configurationService.updateBatch(updates);
            showMessage('success', `${updates.length} configurações guardadas`);
            await loadCategoryConfigs(activeCategory);
        } catch (error) {
            showMessage('error', 'Erro ao guardar configurações');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleEnabled = async (config) => {
        try {
            const enabledKey = `${activeCategory}:Enabled`;
            const isCurrentlyEnabled = configs.find(c => c.key === enabledKey)?.value === 'true';
            
            await configurationService.update(enabledKey, (!isCurrentlyEnabled).toString());
            await loadCategoryConfigs(activeCategory);
            showMessage('success', isCurrentlyEnabled ? 'Desativado' : 'Ativado');
        } catch (error) {
            showMessage('error', 'Erro ao alterar estado');
        }
    };

    const handleTestEmail = async () => {
        setTesting('email');
        try {
            const result = await configurationService.testEmail('test@example.com');
            if (result.success) {
                showMessage('success', result.message);
            } else {
                showMessage('error', result.error);
            }
        } catch (error) {
            showMessage('error', 'Erro ao testar email');
        } finally {
            setTesting(null);
        }
    };

    const handleTestStripe = async () => {
        setTesting('stripe');
        try {
            const result = await configurationService.testStripe();
            if (result.success) {
                showMessage('success', `${result.message} ${result.testMode ? '(Modo Teste)' : '(Modo Produção)'}`);
            } else {
                showMessage('error', result.error);
            }
        } catch (error) {
            showMessage('error', 'Erro ao testar Stripe');
        } finally {
            setTesting(null);
        }
    };

    const handleInitializeDefaults = async () => {
        setSaving(true);
        try {
            await configurationService.initialize();
            showMessage('success', 'Configurações padrão criadas');
            await loadCategories();
        } catch (error) {
            showMessage('error', 'Erro ao inicializar configurações');
        } finally {
            setSaving(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 5000);
    };

    const getCategoryEnabled = () => {
        const enabledKey = `${activeCategory}:Enabled`;
        return configs.find(c => c.key === enabledKey)?.value === 'true';
    };

    const getFieldLabel = (key) => {
        const parts = key.split(':');
        return parts[parts.length - 1];
    };

    const getFieldType = (config) => {
        if (config.isSecret) return 'password';
        if (config.key.includes('Port')) return 'number';
        if (config.key.includes('Enabled') || config.key.includes('UseSsl')) return 'toggle';
        if (config.key.includes('Email') && config.key.includes('Address')) return 'email';
        return 'text';
    };

    if (loading && categories.length === 0) {
        return (
            <div className="admin-config loading">
                <div className="spinner"></div>
                <p>A carregar configurações...</p>
            </div>
        );
    }

    return (
        <div className="admin-config">
            <h1 className="page-title">
                <span className="icon">⚙️</span>
                Configurações do Sistema
            </h1>

            {message && (
                <div className={`message ${message.type}`}>
                    <span>{message.type === 'success' ? '✅' : message.type === 'error' ? '❌' : 'ℹ️'}</span>
                    {message.text}
                    <button onClick={() => setMessage(null)}>×</button>
                </div>
            )}

            <div className="config-layout">
                {/* Categories Sidebar */}
                <div className="categories-sidebar">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(cat.id)}
                        >
                            <span className="cat-icon">{CATEGORY_ICONS[cat.id] || '📋'}</span>
                            <div className="cat-info">
                                <span className="cat-name">{cat.name}</span>
                                <span className="cat-desc">{cat.description}</span>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Config Panel */}
                <div className="config-panel">
                    {activeCategory && (
                        <>
                            <div className="panel-header">
                                <div className="header-info">
                                    <h2>
                                        <span>{CATEGORY_ICONS[activeCategory]}</span>
                                        {categories.find(c => c.id === activeCategory)?.name}
                                    </h2>
                                    <p>{categories.find(c => c.id === activeCategory)?.description}</p>
                                </div>
                                <div className="header-actions">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={getCategoryEnabled()}
                                            onChange={handleToggleEnabled}
                                        />
                                        <span className="slider"></span>
                                        <span className="label">{getCategoryEnabled() ? 'Ativo' : 'Inativo'}</span>
                                    </label>
                                </div>

                                                {activeCategory === 'GoogleFit' && configs.length === 0 && (
                                                    <button
                                                        className="save-btn"
                                                        onClick={handleInitializeDefaults}
                                                        disabled={saving}
                                                        title="Cria as chaves GoogleFit:* na base de dados"
                                                    >
                                                        {saving ? '⏳ ...' : '➕ Criar chaves Google Fit'}
                                                    </button>
                                                )}
                            </div>

                            {loading ? (
                                <div className="loading-panel">
                                    <div className="spinner"></div>
                                </div>
                            ) : (
                                <div className="config-fields">
                                    {activeCategory === 'GoogleFit' && (
                                        <div className="info-box" style={{
                                            background: 'var(--gray-50)',
                                            border: '1px solid var(--gray-200)',
                                            borderRadius: '10px',
                                            padding: '1rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <strong>Redirect URI</strong>
                                            <div style={{ marginTop: '0.25rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {window.location.origin}/api/healthintegration/googlefit/callback
                                            </div>
                                            <div style={{ marginTop: '0.5rem', color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                                                No Google Cloud, coloca este URL exatamente (Authorized redirect URI).
                                            </div>
                                        </div>
                                    )}

                                    {configs
                                        .filter(c => !c.key.endsWith(':Enabled'))
                                        .map(config => (
                                            <div key={config.key} className="config-field">
                                                <label>
                                                    <span className="field-label">{getFieldLabel(config.key)}</span>
                                                    {config.isSecret && <span className="secret-badge">🔒</span>}
                                                </label>
                                                <p className="field-desc">{config.description}</p>
                                                
                                                {getFieldType(config) === 'toggle' ? (
                                                    <label className="toggle-switch small">
                                                        <input
                                                            type="checkbox"
                                                            checked={editValues[config.key] === 'true'}
                                                            onChange={(e) => handleValueChange(config.key, e.target.checked.toString())}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                ) : (
                                                    <input
                                                        type={getFieldType(config)}
                                                        value={editValues[config.key] || ''}
                                                        onChange={(e) => handleValueChange(config.key, e.target.value)}
                                                        placeholder={config.isSecret ? '••••••••' : ''}
                                                        className={config.isSecret ? 'secret-input' : ''}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                </div>
                            )}

                            <div className="panel-footer">
                                <div className="test-buttons">
                                    {activeCategory === 'Email' && (
                                        <button 
                                            className="btn-test"
                                            onClick={handleTestEmail}
                                            disabled={testing === 'email'}
                                        >
                                            {testing === 'email' ? '⏳ A testar...' : '📧 Testar Email'}
                                        </button>
                                    )}
                                    {activeCategory === 'Stripe' && (
                                        <button 
                                            className="btn-test"
                                            onClick={handleTestStripe}
                                            disabled={testing === 'stripe'}
                                        >
                                            {testing === 'stripe' ? '⏳ A testar...' : '💳 Testar Stripe'}
                                        </button>
                                    )}
                                </div>
                                <button 
                                    className="btn-save"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? '⏳ A guardar...' : '💾 Guardar Alterações'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Help Section */}
            <div className="help-section">
                <h3>💡 Ajuda</h3>
                <div className="help-cards">
                    <div className="help-card">
                        <h4>💳 Stripe</h4>
                        <p>Obtenha as chaves em <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">dashboard.stripe.com</a>. Use chaves de teste (sk_test_) para desenvolvimento.</p>
                    </div>
                    <div className="help-card">
                        <h4>📧 Email</h4>
                        <p>Para Gmail, crie uma "App Password" em <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com</a>.</p>
                    </div>
                    <div className="help-card">
                        <h4>📅 Google Calendar</h4>
                        <p>Configure OAuth em <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a>. Ative a Calendar API.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminConfiguration;
